import { useState, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, API, apiError } from "@/lib/api";
import { secureClick } from "@/lib/openFile";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { FolderLock, Upload, Download, Trash2, Loader2, Eye } from "lucide-react";
import { toast } from "sonner";
import { fmtDateTime } from "@/lib/constants";

const CATEGORIE = ["Documento identità", "Decreto", "Nomina", "Attestato", "Certificato medico", "Assicurazione", "Altro"];

export default function DocumentiPersonali({ guardiaId }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const isManager = ["admin", "coordinatore"].includes(user?.role);
  const fileRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [categoria, setCategoria] = useState("Altro");
  const [nome, setNome] = useState("");
  const [file, setFile] = useState(null);

  const { data: docs = [] } = useQuery({
    queryKey: ["doc-personali", guardiaId],
    queryFn: async () => (await api.get(`/guardie/${guardiaId}/documenti`)).data,
    enabled: !!guardiaId,
  });

  const submit = async () => {
    if (!file) { toast.error("Seleziona un file"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("File troppo grande (max 5 MB)"); return; }
    setSaving(true);
    try {
      const data = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(r.result);
        r.onerror = rej;
        r.readAsDataURL(file);
      });
      await api.post(`/guardie/${guardiaId}/documenti`, { nome: nome || file.name, categoria, file_name: file.name, file_data: data });
      toast.success("Documento caricato");
      qc.invalidateQueries({ queryKey: ["doc-personali", guardiaId] });
      setOpen(false); setFile(null); setNome(""); setCategoria("Altro");
      if (fileRef.current) fileRef.current.value = "";
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const viewUrl = (d, dl = false) => `${API}/guardie/${guardiaId}/documenti/${d.id}/view${dl ? "?download=true" : ""}`;

  const remove = async (d) => {
    if (!window.confirm(`Eliminare "${d.nome}"?`)) return;
    try {
      await api.delete(`/guardie/${guardiaId}/documenti/${d.id}`);
      toast.success("Documento eliminato");
      qc.invalidateQueries({ queryKey: ["doc-personali", guardiaId] });
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  return (
    <Card className="mt-6 border-slate-200 shadow-sm" data-testid="documenti-personali">
      <CardContent className="p-6">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-slate-900"><FolderLock className="h-5 w-5 text-[#166534]" /> Documenti riservati</h2>
            <p className="mt-1 text-xs text-slate-500">Visibili solo a te e ai coordinatori. Una volta caricati possono essere eliminati solo da admin/coordinatori.</p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button size="sm" className="bg-[#14382C] hover:bg-[#0E281F]" data-testid="add-doc-personale-button"><Upload className="mr-2 h-4 w-4" /> Carica documento</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Carica documento riservato</DialogTitle></DialogHeader>
              <div className="space-y-4 py-2">
                <div className="space-y-2"><Label>Nome / descrizione</Label><Input data-testid="doc-personale-nome" value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Es. Carta d'identità" /></div>
                <div className="space-y-2">
                  <Label>Categoria</Label>
                  <Select value={categoria} onValueChange={setCategoria}>
                    <SelectTrigger data-testid="doc-personale-categoria"><SelectValue /></SelectTrigger>
                    <SelectContent>{CATEGORIE.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label>File (PDF/immagine, max 5 MB)</Label><Input ref={fileRef} type="file" accept="image/*,.pdf" data-testid="doc-personale-file" onChange={(e) => setFile(e.target.files?.[0] || null)} /></div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
                <Button onClick={submit} disabled={saving} data-testid="save-doc-personale" className="bg-[#14382C] hover:bg-[#0E281F]">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Carica</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
        {docs.length === 0 ? (
          <p className="rounded-lg border border-dashed border-slate-300 bg-slate-50 py-8 text-center text-sm text-slate-400">Nessun documento riservato.</p>
        ) : (
          <div className="divide-y divide-slate-100">
            {docs.map((d) => (
              <div key={d.id} data-testid={`doc-personale-${d.id}`} className="flex items-center justify-between gap-3 py-2.5">
                <div className="min-w-0">
                  <a href={viewUrl(d)} onClick={secureClick(viewUrl(d))} target="_blank" rel="noopener noreferrer" data-testid={`doc-personale-title-${d.id}`} className="block truncate text-sm font-semibold text-slate-800 hover:text-[#166534] hover:underline">{d.nome}</a>
                  <p className="text-xs text-slate-400">{d.categoria} · {fmtDateTime(d.created_at)}</p>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" asChild className="bg-[#14382C] hover:bg-[#0E281F]"><a href={viewUrl(d)} onClick={secureClick(viewUrl(d))} target="_blank" rel="noopener noreferrer" data-testid={`open-doc-${d.id}`} aria-label="Apri documento" title="Apri"><Eye className="h-4 w-4" /><span className="ml-1 hidden sm:inline">Apri</span></a></Button>
                  <Button size="sm" variant="outline" asChild><a href={viewUrl(d, true)} onClick={secureClick(viewUrl(d, true), true)} data-testid={`download-doc-${d.id}`} aria-label="Scarica documento" title="Scarica"><Download className="h-4 w-4" /></a></Button>
                  {isManager && (
                    <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50 hover:text-red-700" data-testid={`delete-doc-${d.id}`} onClick={() => remove(d)}><Trash2 className="h-4 w-4" /></Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
